import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Mountain, Compass, RotateCcw, Sparkles, AlertTriangle, Calendar, Info } from 'lucide-react';
import { formatMoney } from '@/lib/format';
import { fetchFiBreakdown, flattenBreakdownRows } from '@/lib/currency';
import FxBreakdownCard from '@/components/FxBreakdownCard';
import usePageTitle from '@/hooks/usePageTitle';
import useFocusRefetch from '@/hooks/useFocusRefetch';
import { readCache, writeCache, withTimeout } from '@/lib/pageCache';
import { readSharedAssumptions, writeSharedAssumptions } from '@/lib/sharedAssumptions';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_FI = {
  nominal_return_pct: 8,
  inflation_pct: 6,
  withdrawal_rate_pct: 4,
  monthly_savings: null,
};

function formatProjectedDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
  } catch {
    return iso;
  }
}

function yearsFromMonths(m) {
  if (m == null) return null;
  if (m < 12) return `${m} month${m === 1 ? '' : 's'}`;
  const yrs = m / 12;
  return `${yrs.toFixed(yrs < 10 ? 1 : 0)} year${yrs === 1 ? '' : 's'}`;
}

export default function FI() {
  usePageTitle('Financial Independence');
  const { session, user, profile } = useAuth();
  const currency = profile?.currency || 'INR';
  // Phase 30, Safari tab-suspension resilience.
  const uid = user?.id || null;
  const cached = uid ? readCache(uid, 'fi:v1') : null;
  const [data, setData] = useState(cached || null);
  const [loading, setLoading] = useState(!cached);
  const hasLoadedOnceRef = useRef(!!cached);
  // Phase 55 follow-up #8 — guards the debounced auto-recompute effect
  // below. We flip this to true whenever assumptions are populated
  // programmatically (initial mount, load() refetch) so we don't fire
  // a no-op recompute call on the server immediately after fetching
  // current state. Declared up here so `load()` can flip it.
  const skipNextAutoRef = useRef(true);
  const [recomputing, setRecomputing] = useState(false);
  const [showAdjust, setShowAdjust] = useState(false);
  const [assumptions, setAssumptions] = useState(() => {
    // Phase 70 (A2) — Apply user-scoped shared-assumption fallbacks so
    // a user who set 9% return on Projections doesn't see 8% here.
    const shared = uid ? readSharedAssumptions(uid) : null;
    const a = cached?.assumptions || {};
    return {
      nominal_return_pct: a.nominal_return_pct ?? shared?.nominal_return_pct ?? DEFAULT_FI.nominal_return_pct,
      inflation_pct:      a.inflation_pct      ?? shared?.inflation_pct      ?? DEFAULT_FI.inflation_pct,
      withdrawal_rate_pct: a.withdrawal_rate_pct ?? DEFAULT_FI.withdrawal_rate_pct,
      monthly_savings: a.monthly_savings ?? null,
    };
  });
  const [savingsOverride, setSavingsOverride] = useState(() => {
    const a = cached?.assumptions;
    return a?.monthly_savings != null ? String(a.monthly_savings) : '';
  });
  // Phase 70 — Trust-audit fix (sAP feedback):
  // Same fix as Projections.js — track whether the user has touched the
  // assumption controls so silent useFocusRefetch reloads don't wipe
  // in-progress edits. See Projections.js for the full rationale.
  const assumptionsDirtyRef = useRef(false);
  const markAssumptionsDirty = () => { assumptionsDirtyRef.current = true; };
  const clearAssumptionsDirty = () => { assumptionsDirtyRef.current = false; };
  // Phase 38.5, corpus breakdown across native currencies.
  const [breakdown, setBreakdown] = useState(null);

  const loadBreakdown = async () => {
    if (!session?.access_token) return;
    const d = await fetchFiBreakdown(session.access_token);
    if (d) setBreakdown(d);
  };

  const load = async ({ silent = false } = {}) => {
    if (!session?.access_token) return;
    if (!silent && !hasLoadedOnceRef.current) setLoading(true);
    try {
      const r = await withTimeout(
        axios.get(`${BACKEND_URL}/api/fi/current`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        }),
        12000, 'fi.current',
      );
      setData(r.data);
      const a = r.data?.assumptions || DEFAULT_FI;
      // Phase 70 (trust-audit): only adopt server-side assumptions when
      // the user hasn't started editing locally. Mirrors Projections.js.
      // The skipNextAutoRef flag also guards against the debounced
      // auto-recompute firing a no-op immediately after we hydrate.
      const shouldAdoptServer = !silent || !hasLoadedOnceRef.current || !assumptionsDirtyRef.current;
      if (shouldAdoptServer) {
        // Skip the next debounced auto-recompute — these assumptions
        // just came back from the server, no need to immediately ask it
        // to recompute the same numbers.
        skipNextAutoRef.current = true;
        // Phase 70 (A2) — fall back to user-scoped shared store for
        // return/inflation only when the server didn't supply them.
        const shared = uid ? readSharedAssumptions(uid) : null;
        setAssumptions({
          nominal_return_pct: a.nominal_return_pct ?? shared?.nominal_return_pct ?? DEFAULT_FI.nominal_return_pct,
          inflation_pct:      a.inflation_pct      ?? shared?.inflation_pct      ?? DEFAULT_FI.inflation_pct,
          withdrawal_rate_pct: a.withdrawal_rate_pct ?? DEFAULT_FI.withdrawal_rate_pct,
          monthly_savings: a.monthly_savings ?? null,
        });
        if (a.monthly_savings != null) setSavingsOverride(String(a.monthly_savings));
        // Adopting server values resets dirty state.
        assumptionsDirtyRef.current = false;
      }
      if (user?.id) writeCache(user.id, 'fi:v1', r.data);
      hasLoadedOnceRef.current = true;
    } catch (e) {
      // Timeout / 5xx: keep prior data, only toast on initial cold load.
      if (!hasLoadedOnceRef.current) {
        toast.error(e?.response?.data?.detail || 'Could not load FI tracker.');
      } else {
        console.warn('FI load failed (keeping prior data):', e?.message || e);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (!session?.access_token) return;
    load();
    loadBreakdown();
    // Phase 29.1, Refocus resilience: depend on stable `session?.user?.id`,
    // not the full session object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);
  useFocusRefetch(() => { load({ silent: true }); loadBreakdown(); }, { minHiddenMs: 60_000 });

  const recompute = async (next) => {
    if (!session?.access_token) return;
    setRecomputing(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/fi/recompute`, {
        assumptions: next,
      }, {
        headers: { Authorization: `Bearer ${session.access_token}` },
        timeout: 20000,
      });
      setData(r.data);
      toast.success('FI view updated.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not recompute.');
    } finally {
      setRecomputing(false);
    }
  };

  const handleApply = () => {
    const savNum = savingsOverride === '' ? null : Number(savingsOverride);
    recompute({
      nominal_return_pct: assumptions.nominal_return_pct,
      inflation_pct: assumptions.inflation_pct,
      withdrawal_rate_pct: assumptions.withdrawal_rate_pct,
      monthly_savings: (savNum === null || Number.isNaN(savNum)) ? null : savNum,
    });
    clearAssumptionsDirty();
    // Phase 70 (A2) — mirror shared return/inflation so Projections picks them up.
    if (uid) writeSharedAssumptions(uid, {
      nominal_return_pct: assumptions.nominal_return_pct,
      inflation_pct: assumptions.inflation_pct,
    });
  };

  // Phase 55 follow-up #8 — debounced auto-recompute when sliders move.
  // Beta users reported "I tried changing inputs but the FI target
  // didn't update", because changes were committed locally but
  // recompute() was only called on Apply click. We now fire recompute
  // 400ms after the last assumption change. Apply remains as the
  // explicit one-shot CTA (also useful when entering a custom
  // monthly_savings via the text input).
  //
  // Guard against the initial mount (assumptions arrive from the load()
  // response, not a user gesture) by checking the skip ref declared
  // up at the top of this component.
  useEffect(() => {
    if (skipNextAutoRef.current) {
      skipNextAutoRef.current = false;
      return;
    }
    if (!session?.access_token) return;
    const t = setTimeout(() => {
      const savNum = savingsOverride === '' ? null : Number(savingsOverride);
      recompute({
        nominal_return_pct: assumptions.nominal_return_pct,
        inflation_pct: assumptions.inflation_pct,
        withdrawal_rate_pct: assumptions.withdrawal_rate_pct,
        monthly_savings: (savNum === null || Number.isNaN(savNum)) ? null : savNum,
      });
      // Phase 70 — once the debounced save fires the server holds the
      // user's intent, so a subsequent silent refetch can safely adopt
      // the same values without "wiping" anything.
      clearAssumptionsDirty();
      // Phase 70 (A2) — mirror shared return/inflation each time the
      // user's effective values settle on FI, so Projections picks them
      // up on its next load.
      if (uid) writeSharedAssumptions(uid, {
        nominal_return_pct: assumptions.nominal_return_pct,
        inflation_pct: assumptions.inflation_pct,
      });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    assumptions.nominal_return_pct,
    assumptions.inflation_pct,
    assumptions.withdrawal_rate_pct,
    savingsOverride,
  ]);

  const handleReset = () => {
    setAssumptions(DEFAULT_FI);
    setSavingsOverride('');
    recompute(DEFAULT_FI);
    clearAssumptionsDirty();
  };

  if (loading) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-40 rounded-2xl" />
        <Skeleton className="h-72 rounded-2xl" />
      </div>
    );
  }

  const inputs = data?.inputs || {};
  const outputs = data?.outputs || {};
  const preliminary = !!outputs.preliminary;
  const progressPct = Math.min(Number(outputs.progress_pct || 0), 100);
  const fiReached = Number(outputs.projected_months || 0) === 0 && (outputs.current_corpus || 0) >= (outputs.fi_target || 0) && (outputs.fi_target || 0) > 0;

  return (
    <div className="space-y-5" data-testid="fi-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Financial Independence</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            A clear picture of when work becomes optional. Based on your trailing annual expenses and a
            calm withdrawal rate, adjust the assumptions any time.
          </p>
        </div>
        <Button
          variant={showAdjust ? 'secondary' : 'ghost'}
          onClick={() => setShowAdjust((v) => !v)}
          className="rounded-xl"
          data-testid="fi-toggle-adjust"
        >
          <Compass className="h-4 w-4 mr-1" /> {showAdjust ? 'Hide assumptions' : 'Adjust assumptions'}
        </Button>
      </div>

      {/* Preliminary disclaimer */}
      {preliminary && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground rounded-xl border border-[hsl(var(--gold))]/30 bg-[hsl(var(--gold))]/8 px-3 py-2" data-testid="fi-preliminary">
          <AlertTriangle className="h-3.5 w-3.5 text-[hsl(var(--gold))]" />
          <span>This is a preliminary estimate, we have {inputs.months_of_data || 'limited'} month{inputs.months_of_data === 1 ? '' : 's'} of expense data. The FI target will steady as a full year accumulates.</span>
        </div>
      )}

      {/* Hero, target + progress */}
      <Card className="rounded-3xl border-[hsl(var(--gold))]/30 bg-card shadow-brand-lg overflow-hidden" data-testid="fi-hero-card">
        <CardContent className="p-6 sm:p-8 space-y-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-2xl bg-[hsl(var(--gold))]/15 text-[hsl(var(--gold))] grid place-items-center">
                <Mountain className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <span>FI target ({Number(outputs.multiplier || 25).toFixed(0)}× annual expenses)</span>
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label="What is the 25× rule?"
                          className="inline-flex items-center justify-center text-muted-foreground/70 hover:text-foreground focus-visible:text-foreground focus-visible:outline-none cursor-help"
                          data-testid="fi-target-info"
                        >
                          <Info className="h-3 w-3" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs text-xs leading-relaxed">
                        The 25× rule estimates how much you need to be financially independent: annual expenses × 25. It assumes a ~4% safe withdrawal rate. Adjust the multiplier below for a more conservative or aggressive target.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="font-display text-3xl sm:text-4xl font-semibold tabular-nums" data-testid="fi-target-value">
                  {formatMoney(outputs.fi_target || 0, currency)}
                </div>
                <div className="text-xs text-muted-foreground">
                  Based on annual expenses of <span className="font-medium">{formatMoney(inputs.annual_expenses || 0, currency, { compact: true })}</span>
                </div>
              </div>
            </div>
            <Badge variant="outline" className="rounded-full text-[10px]">{data?.cached ? 'Cached' : 'Fresh'}</Badge>
          </div>

          {/* Progress bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <div className="text-muted-foreground">Progress</div>
              <div className="font-medium tabular-nums" data-testid="fi-progress-pct">{progressPct.toFixed(1)}%</div>
            </div>
            <Progress value={progressPct} className="h-2.5" data-testid="fi-progress-bar" />
            <div className="flex items-center justify-between text-[11px] text-muted-foreground">
              <span>{formatMoney(outputs.current_corpus || 0, currency, { compact: true })}</span>
              <span>{formatMoney(outputs.fi_target || 0, currency, { compact: true })}</span>
            </div>
          </div>

          {/* Projected date / years */}
          <div className="grid sm:grid-cols-2 gap-3 pt-2">
            <div className="rounded-2xl border border-border/70 px-4 py-3">
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" /> Projected milestone
              </div>
              <div className="font-display text-xl font-semibold mt-1" data-testid="fi-projected-date">
                {fiReached
                  ? 'Already there'
                  : (formatProjectedDate(outputs.projected_date) || 'Beyond our calm horizon')}
              </div>
              <div className="text-[11px] text-muted-foreground">
                {fiReached
                  ? 'Your corpus already covers your trailing expenses at this withdrawal rate.'
                  : (outputs.projected_months != null
                    ? `${yearsFromMonths(outputs.projected_months)} at this rhythm.`
                    : 'Increase savings or returns to bring this into view.')}
              </div>
            </div>
            <div className="rounded-2xl border border-border/70 px-4 py-3">
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5" /> <span>Monthly contribution used</span>
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label="What is the monthly contribution used?"
                        className="inline-flex items-center justify-center text-muted-foreground/70 hover:text-foreground focus-visible:text-foreground focus-visible:outline-none cursor-help"
                        data-testid="fi-monthly-contrib-info"
                      >
                        <Info className="h-3 w-3" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs text-xs leading-relaxed">
                      The monthly amount we project you'll invest toward FI, derived from your recent income, expenses, and savings rate. Override it in the controls below if your trajectory is shifting.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="font-display text-xl font-semibold tabular-nums mt-1" data-testid="fi-monthly-contrib">
                {formatMoney(outputs.monthly_contribution || 0, currency, { compact: true })}
              </div>
              <div className="text-[11px] text-muted-foreground">
                Derived from your recent snapshots. Override below if your trajectory is shifting.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Phase 38.5, multi-currency corpus disclosure. Self-hides when
          the user has a single currency. Surfaces which native-currency
          pots feed the FI target headline above. */}
      <FxBreakdownCard
        title="Corpus breakdown"
        base={breakdown?.base_currency || currency}
        breakdown={flattenBreakdownRows(breakdown?.breakdown, ['net'])}
        headline={breakdown}
        valueColumns={[{ key: 'net', label: 'Corpus', nativeKey: 'native_net', baseKey: 'base_net' }]}
        warnings={breakdown?.warnings || []}
        testId="fi-fx-breakdown"
      />

      {/* Adjust assumptions */}
      {showAdjust && (
        <Card className="rounded-2xl border-border/70" data-testid="fi-adjust-panel">
          <CardContent className="p-5 space-y-5">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground">Withdrawal rate</Label>
                  <span className="text-xs font-medium tabular-nums" data-testid="fi-adjust-wr-value">{Number(assumptions.withdrawal_rate_pct).toFixed(1)}%</span>
                </div>
                <Slider
                  value={[Number(assumptions.withdrawal_rate_pct)]}
                  onValueChange={(v) => { setAssumptions((a) => ({ ...a, withdrawal_rate_pct: v[0] })); markAssumptionsDirty(); }}
                  min={2.5} max={6} step={0.25}
                  data-testid="fi-adjust-wr-slider"
                />
                <div className="text-[10px] text-muted-foreground">4% = the classic 25× rule. Lower = more conservative.</div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground">Nominal return</Label>
                  <span className="text-xs font-medium tabular-nums" data-testid="fi-adjust-return-value">{Number(assumptions.nominal_return_pct).toFixed(1)}%</span>
                </div>
                <Slider
                  value={[Number(assumptions.nominal_return_pct)]}
                  onValueChange={(v) => { setAssumptions((a) => ({ ...a, nominal_return_pct: v[0] })); markAssumptionsDirty(); }}
                  min={0} max={20} step={0.5}
                  data-testid="fi-adjust-return-slider"
                />
                <div className="text-[10px] text-muted-foreground">Long-term average return on your corpus.</div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground">Inflation</Label>
                  <span className="text-xs font-medium tabular-nums" data-testid="fi-adjust-inflation-value">{Number(assumptions.inflation_pct).toFixed(1)}%</span>
                </div>
                <Slider
                  value={[Number(assumptions.inflation_pct)]}
                  onValueChange={(v) => { setAssumptions((a) => ({ ...a, inflation_pct: v[0] })); markAssumptionsDirty(); }}
                  min={0} max={15} step={0.5}
                  data-testid="fi-adjust-inflation-slider"
                />
                <div className="text-[10px] text-muted-foreground">Provides context for purchasing power over time.</div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground">Monthly savings (override)</Label>
                  <span className="text-[10px] text-muted-foreground">{formatMoney(inputs.avg_monthly_savings || 0, currency, { compact: true })} avg</span>
                </div>
                <Input
                  type="number"
                  value={savingsOverride}
                  onChange={(e) => { setSavingsOverride(e.target.value); markAssumptionsDirty(); }}
                  placeholder="Leave blank to use average"
                  className="rounded-xl h-9"
                  data-testid="fi-adjust-savings-input"
                />
                <div className="text-[10px] text-muted-foreground">Override what you save each month.</div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={handleReset} className="rounded-xl" data-testid="fi-adjust-reset"><RotateCcw className="h-4 w-4 mr-1" /> Reset</Button>
              <Button onClick={handleApply} disabled={recomputing} className="rounded-xl" data-testid="fi-adjust-apply">
                <Sparkles className="h-4 w-4 mr-1" /> {recomputing ? 'Recomputing…' : 'Apply'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Inputs summary */}
      <Card className="rounded-2xl border-border/70 bg-card">
        <CardContent className="p-5 grid sm:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-[11px] text-muted-foreground">Trailing annual expenses</div>
            <div className="font-display text-lg font-semibold tabular-nums" data-testid="fi-input-expenses">
              {formatMoney(inputs.annual_expenses || 0, currency, { compact: true })}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground">Current corpus</div>
            <div className="font-display text-lg font-semibold tabular-nums" data-testid="fi-input-corpus">
              {formatMoney(inputs.current_corpus || 0, currency, { compact: true })}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground">Avg monthly savings</div>
            <div className="font-display text-lg font-semibold tabular-nums" data-testid="fi-input-savings">
              {formatMoney(inputs.avg_monthly_savings || 0, currency, { compact: true })}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="text-[11px] text-muted-foreground text-center pt-2">
        FI is a steady goalpost, not a finish line. Markets shift; your rhythm and intent matter most.
      </div>
    </div>
  );
}
